import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/cn";
import { getProvider, updateProvider, type ProviderConfig } from "@/api/providers";
import {
  ProviderHeroCard,
  ProviderMetadataSections,
  ProviderHowItWorks,
} from "@/components/providers/provider-detail-sections";
import { isScannableAtsProvider, isScannableBoardProvider } from "@/lib/provider-ui";

function SettingsProviderSidebar({ provider }: { provider: ProviderConfig }) {
  const primaryAction =
    provider.type === "board"
      ? { to: `/explore?mode=boards`, label: "Manage in Find Jobs" }
      : provider.type === "ats"
        ? { to: "/explore?sources=1", label: "Configure My sources" }
        : { to: "/settings/cli", label: "CLI settings" };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Actions
        </h3>
        <div className="flex flex-col gap-2">
          <Link
            to={primaryAction.to}
            className="rounded-md bg-brand px-3 py-2 text-center text-sm font-medium text-brand-foreground transition hover:opacity-90"
          >
            {primaryAction.label}
          </Link>
          {provider.website && (
            <a
              href={provider.website}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
            >
              Visit website
            </a>
          )}
          {provider.docsUrl && (
            <a
              href={provider.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-border px-3 py-2 text-center text-sm text-foreground transition hover:bg-muted/20"
            >
              View documentation
            </a>
          )}
        </div>
      </div>

      {!provider.enabled && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-600 dark:text-amber-400">
          This provider is globally disabled. Enable it here before adding it to your scan or
          tracked companies.
        </div>
      )}

      <ProviderHowItWorks type={provider.type} />
    </div>
  );
}

export function SettingsProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data: provider, isLoading, error } = useQuery<ProviderConfig>({
    queryKey: ["provider", id],
    queryFn: () => getProvider(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => updateProvider(id!, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provider", id] });
      qc.invalidateQueries({ queryKey: ["providers"] });
    },
  });

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <Link
          to="/settings/providers"
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          &larr; Back to providers
        </Link>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load provider.</p>}

      {provider && (
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          <div className="mb-6 space-y-4 lg:col-span-1 lg:mb-0">
            <SettingsProviderSidebar provider={provider} />
          </div>

          <div className="space-y-4 lg:col-span-2">
            <ProviderHeroCard
              provider={provider}
              trailing={
                <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={provider.enabled}
                    onChange={() => toggleMutation.mutate(!provider.enabled)}
                    disabled={toggleMutation.isPending}
                    className="peer sr-only"
                  />
                  <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
                </label>
              }
              badges={
                <>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px]",
                      provider.enabled
                        ? "bg-success/10 text-success"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {provider.enabled ? "Enabled" : "Disabled"}
                  </span>
                  {isScannableAtsProvider(provider) && (
                    <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-medium text-brand">
                      Company Scan
                    </span>
                  )}
                  {isScannableBoardProvider(provider) && (
                    <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-medium text-brand">
                      Board Scan
                    </span>
                  )}
                </>
              }
            />
            <ProviderMetadataSections provider={provider} />
          </div>
        </div>
      )}
    </div>
  );
}
